<script setup lang="ts">
import { ref, computed, onMounted, toRefs, watch } from 'vue';
import { useRouting } from '@/composables';
import { useTenantIntegrationStore, useTenantIntegrationSISSchoolDayStore } from '@/store';
import SchoolDaySISOrganizationsActions from './SchoolDaySISOrganizationsActions.vue';
import SchoolDaySISOrganizationModal from './SchoolDaySISOrganizationModal.vue';

interface PropsInterface {
  integration?: null;
}

const props = withDefaults(defineProps<PropsInterface>(), {
  integration: null,
});

const tenantIntegrationStore = useTenantIntegrationStore();

const tenantIntegrationSISSchoolDayStore = useTenantIntegrationSISSchoolDayStore();

const { integration } = toRefs(props);

const $routing = useRouting();

const organizations = ref([]);

const connection = ref(null);

const tenantId = ref(Number($routing.route.params.id));

watch(tenantIntegrationStore.integrations, () => init());

onMounted(() => init());

const init = () => defineIntegration();

const defineIntegration = () => {
  if (integration.value) organizations.value = integration.value.integration_organizations;
};

const totalOrganizations = computed(() => (organizations.value.length ? organizations.value.length : '0'));

const onConnectOrganizationsCreate = () => {
  tenantIntegrationStore.modalAction = 'create';
  tenantIntegrationStore.openModal = true;
};

const onConnectOrganizationsUpdate = (id: number) => {
  connection.value = integration.value.integration_organizations.find((item) => item.id === id);
  tenantIntegrationStore.modalAction = 'update';
  tenantIntegrationStore.openModal = true;
};

const onConnectOrganizationsCancel = () => {
  tenantIntegrationStore.openModal = false;
  connection.value = null;
};

const onConnectOrganizationDataSync = async (id: number) => tenantIntegrationSISSchoolDayStore.sync(tenantId.value, id);
</script>

<template>
  <div v-if="integration" class="field p-3 pb-0 border-1 border-gray-100 border-round-md">
    <div class="flex overflow-hidden">
      <div class="flex-none flex">
        <h2 class="pb-3">
          <span>Organizations</span>
          <Tag :value="totalOrganizations" rounded class="ml-2 p-2 border-circle w-1rem h-1rem text-xs text-white" />
        </h2>
      </div>
      <div class="flex-grow-1 flex"></div>
      <div class="flex-none flex">
        <AppButton
          type="text"
          size="small"
          text="Add Organization Connection"
          icon-left="add"
          @click="onConnectOrganizationsCreate"
        />
      </div>
    </div>
    <SchoolDaySISOrganizationModal
      v-if="tenantIntegrationStore.openModal"
      :connection="connection"
      :integration="integration"
      :tenant-id="tenantId"
      @cancel="onConnectOrganizationsCancel"
    />
    <DataTable :value="organizations" data-key="id" scrollable scroll-height="400px">
      <template v-if="organizations.length">
        <Column header="Organization" field="name" header-style="width: 5em">
          <template #body="{ data }">
            <RouterLink
              :to="`/organizations/${data.organization_id}`"
              class="font-semibold"
              target="_blank"
              v-text="data.organization_name"
            />
          </template>
        </Column>
        <Column header="SIS Organization" field="organization_sis_name" />
        <Column
          field="actions"
          header="Actions"
          body-style="justify-content: center; font-size: .8725rem; max-width: 10rem;"
          header-class="justify-content-center mw-10rem"
        >
          <template #body="{ data }">
            <SchoolDaySISOrganizationsActions
              :data="data"
              :integration="integration"
              :tenant-id="tenantId"
              @sync="onConnectOrganizationDataSync(data.id)"
              @update="onConnectOrganizationsUpdate"
            />
          </template>
        </Column>
      </template>
      <template #empty>There are no organizations connected</template>
    </DataTable>
  </div>
</template>

<style lang="scss" scoped>
.p-tag {
  padding: 10px !important;
}
</style>
